export type SchemaKind = "Article" | "Product" | "LocalBusiness" | "Organization" | "FAQPage";
export type Level = "pass" | "warn" | "fail" | "info";

export interface FieldDef {
  key: string;
  label: string;
  required: boolean;
  recommended: boolean;
  placeholder: string;
  input: "text" | "url" | "date" | "textarea" | "select";
  options?: string[];
}

export interface KindDef {
  kind: SchemaKind;
  label: string;
  description: string;
  googleDoc: string;
  schemaDoc: string;
  support: "Rich result eligible" | "Vocabulary support" | "Limited surface";
  fields: FieldDef[];
}

export interface ReadinessCheck {
  key: string;
  level: Level;
  title: string;
  detail: string;
}

export interface Readiness {
  score: number;
  label: string;
  requiredDone: number;
  requiredTotal: number;
  recommendedDone: number;
  recommendedTotal: number;
  checks: ReadinessCheck[];
}

export type Draft = Record<string, string>;

export const kindDefs: KindDef[] = [
  {
    kind: "Article",
    label: "Article",
    description: "Blog, news, and editorial pages with author, image, and date signals.",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/article",
    schemaDoc: "https://schema.org/Article",
    support: "Rich result eligible",
    fields: [
      { key: "url", label: "Page URL", required: true, recommended: false, placeholder: "https://example.com/article", input: "url" },
      { key: "headline", label: "Headline", required: true, recommended: false, placeholder: "How to launch a static site", input: "text" },
      { key: "description", label: "Description", required: false, recommended: true, placeholder: "A concise article summary.", input: "textarea" },
      { key: "image", label: "Image URL", required: true, recommended: false, placeholder: "https://example.com/article.jpg", input: "url" },
      { key: "authorName", label: "Author name", required: true, recommended: false, placeholder: "Alex Kim", input: "text" },
      { key: "publisherName", label: "Publisher", required: false, recommended: true, placeholder: "Example Studio", input: "text" },
      { key: "publisherLogo", label: "Publisher logo", required: false, recommended: true, placeholder: "https://example.com/logo.png", input: "url" },
      { key: "datePublished", label: "Date published", required: true, recommended: false, placeholder: "2026-06-14", input: "date" },
      { key: "dateModified", label: "Date modified", required: false, recommended: true, placeholder: "2026-06-14", input: "date" }
    ]
  },
  {
    kind: "Product",
    label: "Product",
    description: "Product snippet markup with offer, brand, price, availability, and rating fields.",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/product",
    schemaDoc: "https://schema.org/Product",
    support: "Rich result eligible",
    fields: [
      { key: "url", label: "Product URL", required: true, recommended: false, placeholder: "https://example.com/product", input: "url" },
      { key: "name", label: "Product name", required: true, recommended: false, placeholder: "Portable Desk Lamp", input: "text" },
      { key: "description", label: "Description", required: true, recommended: false, placeholder: "A compact lamp for focused work.", input: "textarea" },
      { key: "image", label: "Image URL", required: true, recommended: false, placeholder: "https://example.com/lamp.jpg", input: "url" },
      { key: "brand", label: "Brand", required: false, recommended: true, placeholder: "Northline", input: "text" },
      { key: "sku", label: "SKU", required: false, recommended: true, placeholder: "LAMP-100", input: "text" },
      { key: "price", label: "Price", required: true, recommended: false, placeholder: "49.00", input: "text" },
      { key: "currency", label: "Currency", required: true, recommended: false, placeholder: "USD", input: "text" },
      { key: "availability", label: "Availability", required: true, recommended: false, placeholder: "InStock", input: "select", options: ["InStock", "OutOfStock", "PreOrder", "LimitedAvailability"] },
      { key: "ratingValue", label: "Rating value", required: false, recommended: true, placeholder: "4.7", input: "text" },
      { key: "reviewCount", label: "Review count", required: false, recommended: true, placeholder: "128", input: "text" }
    ]
  },
  {
    kind: "LocalBusiness",
    label: "LocalBusiness",
    description: "Business profile markup for address, phone, hours, image, and sameAs links.",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/local-business",
    schemaDoc: "https://schema.org/LocalBusiness",
    support: "Rich result eligible",
    fields: [
      { key: "name", label: "Business name", required: true, recommended: false, placeholder: "Harbor Dental", input: "text" },
      { key: "url", label: "Website URL", required: true, recommended: false, placeholder: "https://example.com", input: "url" },
      { key: "image", label: "Image URL", required: false, recommended: true, placeholder: "https://example.com/storefront.jpg", input: "url" },
      { key: "telephone", label: "Telephone", required: true, recommended: false, placeholder: "+1-555-0100", input: "text" },
      { key: "streetAddress", label: "Street address", required: true, recommended: false, placeholder: "100 Market Street", input: "text" },
      { key: "addressLocality", label: "City", required: true, recommended: false, placeholder: "San Francisco", input: "text" },
      { key: "addressRegion", label: "Region", required: true, recommended: false, placeholder: "CA", input: "text" },
      { key: "postalCode", label: "Postal code", required: true, recommended: false, placeholder: "94105", input: "text" },
      { key: "addressCountry", label: "Country", required: true, recommended: false, placeholder: "US", input: "text" },
      { key: "openingHours", label: "Opening hours", required: false, recommended: true, placeholder: "Mo-Fr 09:00-17:00", input: "text" },
      { key: "priceRange", label: "Price range", required: false, recommended: true, placeholder: "$$", input: "text" },
      { key: "sameAs", label: "SameAs URLs", required: false, recommended: true, placeholder: "https://www.instagram.com/example, https://www.linkedin.com/company/example", input: "textarea" }
    ]
  },
  {
    kind: "Organization",
    label: "Organization",
    description: "Organization identity markup with logo, URL, contact, and social profile links.",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/organization",
    schemaDoc: "https://schema.org/Organization",
    support: "Rich result eligible",
    fields: [
      { key: "name", label: "Organization name", required: true, recommended: false, placeholder: "Example Studio", input: "text" },
      { key: "url", label: "Website URL", required: true, recommended: false, placeholder: "https://example.com", input: "url" },
      { key: "logo", label: "Logo URL", required: true, recommended: false, placeholder: "https://example.com/logo.png", input: "url" },
      { key: "contactType", label: "Contact type", required: false, recommended: true, placeholder: "customer support", input: "text" },
      { key: "telephone", label: "Telephone", required: false, recommended: true, placeholder: "+1-555-0100", input: "text" },
      { key: "sameAs", label: "SameAs URLs", required: false, recommended: true, placeholder: "https://x.com/example, https://www.linkedin.com/company/example", input: "textarea" }
    ]
  },
  {
    kind: "FAQPage",
    label: "FAQPage",
    description: "Question and answer markup for pages where visible FAQ content matches the JSON-LD.",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/faqpage",
    schemaDoc: "https://schema.org/FAQPage",
    support: "Limited surface",
    fields: [
      { key: "pageUrl", label: "Page URL", required: true, recommended: false, placeholder: "https://example.com/faq", input: "url" },
      { key: "question1", label: "Question 1", required: true, recommended: false, placeholder: "What is JSON-LD?", input: "text" },
      { key: "answer1", label: "Answer 1", required: true, recommended: false, placeholder: "JSON-LD is a linked data format that uses JSON.", input: "textarea" },
      { key: "question2", label: "Question 2", required: false, recommended: true, placeholder: "Where do I paste schema markup?", input: "text" },
      { key: "answer2", label: "Answer 2", required: false, recommended: true, placeholder: "Paste the script block into the HTML head or body.", input: "textarea" },
      { key: "question3", label: "Question 3", required: false, recommended: true, placeholder: "How should I test it?", input: "text" },
      { key: "answer3", label: "Answer 3", required: false, recommended: true, placeholder: "Use the Rich Results Test and Schema.org validator.", input: "textarea" }
    ]
  }
];

export const starterDrafts: Record<SchemaKind, Draft> = {
  Article: {
    url: "https://example.com/guides/cloudflare-pages-launch",
    headline: "Cloudflare Pages Launch Checklist",
    description: "A practical checklist for publishing a fast static site with clean metadata and structured data.",
    image: "https://example.com/images/launch-checklist.jpg",
    authorName: "Mina Park",
    publisherName: "Example Studio",
    publisherLogo: "https://example.com/logo.png",
    datePublished: "2026-06-14",
    dateModified: "2026-06-14"
  },
  Product: {
    url: "https://example.com/products/focus-lamp",
    name: "Focus Lamp",
    description: "A compact desk lamp with adjustable warm light for focused work sessions.",
    image: "https://example.com/images/focus-lamp.jpg",
    brand: "Northline",
    sku: "FOCUS-LAMP-01",
    price: "49.00",
    currency: "USD",
    availability: "InStock",
    ratingValue: "4.7",
    reviewCount: "128"
  },
  LocalBusiness: {
    name: "Harbor Dental",
    url: "https://example.com",
    image: "https://example.com/images/storefront.jpg",
    telephone: "+1-555-0100",
    streetAddress: "100 Market Street",
    addressLocality: "San Francisco",
    addressRegion: "CA",
    postalCode: "94105",
    addressCountry: "US",
    openingHours: "Mo-Fr 09:00-17:00",
    priceRange: "$$",
    sameAs: "https://www.instagram.com/example, https://www.linkedin.com/company/example"
  },
  Organization: {
    name: "Example Studio",
    url: "https://example.com",
    logo: "https://example.com/logo.png",
    contactType: "customer support",
    telephone: "+1-555-0100",
    sameAs: "https://x.com/example, https://www.linkedin.com/company/example"
  },
  FAQPage: {
    pageUrl: "https://example.com/faq",
    question1: "What is JSON-LD?",
    answer1: "JSON-LD is a linked data format that uses JSON to describe entities and relationships.",
    question2: "Where do I paste schema markup?",
    answer2: "Paste the script block into the page HTML where your site template allows custom code.",
    question3: "How should I test it?",
    answer3: "Use the Rich Results Test and Schema.org validator after publishing the page."
  }
};

export function getKind(kind: SchemaKind): KindDef {
  return kindDefs.find((item) => item.kind === kind) ?? kindDefs[0];
}

export function buildSchema(kind: SchemaKind, draft: Draft): Record<string, unknown> {
  if (kind === "Article") {
    return compactObject({
      "@context": "https://schema.org",
      "@type": "Article",
      mainEntityOfPage: draft.url,
      headline: draft.headline,
      description: draft.description,
      image: draft.image ? [draft.image] : [],
      author: draft.authorName ? { "@type": "Person", name: draft.authorName } : undefined,
      publisher: draft.publisherName
        ? {
            "@type": "Organization",
            name: draft.publisherName,
            logo: draft.publisherLogo ? { "@type": "ImageObject", url: draft.publisherLogo } : undefined
          }
        : undefined,
      datePublished: draft.datePublished,
      dateModified: draft.dateModified || draft.datePublished
    });
  }

  if (kind === "Product") {
    return compactObject({
      "@context": "https://schema.org",
      "@type": "Product",
      name: draft.name,
      image: draft.image ? [draft.image] : [],
      description: draft.description,
      sku: draft.sku,
      brand: draft.brand ? { "@type": "Brand", name: draft.brand } : undefined,
      offers:
        draft.price || draft.currency || draft.availability
          ? {
              "@type": "Offer",
              url: draft.url,
              priceCurrency: draft.currency,
              price: draft.price,
              availability: draft.availability ? `https://schema.org/${draft.availability}` : undefined
            }
          : undefined,
      aggregateRating:
        draft.ratingValue && draft.reviewCount
          ? {
              "@type": "AggregateRating",
              ratingValue: Number(draft.ratingValue),
              reviewCount: Number(draft.reviewCount)
            }
          : undefined
    });
  }

  if (kind === "LocalBusiness") {
    return compactObject({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: draft.name,
      image: draft.image,
      url: draft.url,
      telephone: draft.telephone,
      priceRange: draft.priceRange,
      openingHours: draft.openingHours,
      sameAs: splitList(draft.sameAs),
      address: {
        "@type": "PostalAddress",
        streetAddress: draft.streetAddress,
        addressLocality: draft.addressLocality,
        addressRegion: draft.addressRegion,
        postalCode: draft.postalCode,
        addressCountry: draft.addressCountry
      }
    });
  }

  if (kind === "Organization") {
    return compactObject({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: draft.name,
      url: draft.url,
      logo: draft.logo,
      sameAs: splitList(draft.sameAs),
      contactPoint:
        draft.telephone || draft.contactType
          ? {
              "@type": "ContactPoint",
              telephone: draft.telephone,
              contactType: draft.contactType
            }
          : undefined
    });
  }

  return compactObject({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntityOfPage: draft.pageUrl,
    mainEntity: [1, 2, 3]
      .map((index) => {
        const question = draft[`question${index}`];
        const answer = draft[`answer${index}`];
        if (!question || !answer) return undefined;
        return {
          "@type": "Question",
          name: question,
          acceptedAnswer: {
            "@type": "Answer",
            text: answer
          }
        };
      })
      .filter(Boolean)
  });
}

export function buildReadiness(kindDef: KindDef, draft: Draft): Readiness {
  const required = kindDef.fields.filter((field) => field.required);
  const recommended = kindDef.fields.filter((field) => field.recommended);
  const requiredDone = required.filter((field) => isFilled(draft[field.key])).length;
  const recommendedDone = recommended.filter((field) => isFilled(draft[field.key])).length;
  const checks: ReadinessCheck[] = [];
  const requiredScore = required.length ? (requiredDone / required.length) * 45 : 45;
  const recommendedScore = recommended.length ? (recommendedDone / recommended.length) * 20 : 20;
  const jsonScore = 15;
  const supportScore = kindDef.support === "Limited surface" ? 6 : 10;
  const handoffScore = 10;
  const score = Math.round(requiredScore + recommendedScore + jsonScore + supportScore + handoffScore);

  checks.push({
    key: "required",
    level: requiredDone === required.length ? "pass" : "fail",
    title: "Required fields",
    detail: `${requiredDone}/${required.length} core fields complete.`
  });
  checks.push({
    key: "recommended",
    level: recommendedDone === recommended.length ? "pass" : recommendedDone ? "warn" : "info",
    title: "Recommended fields",
    detail: `${recommendedDone}/${recommended.length} enhancement fields complete.`
  });
  checks.push({
    key: "json",
    level: "pass",
    title: "JSON-LD syntax",
    detail: "The generated object can be serialized as JSON-LD."
  });
  checks.push({
    key: "support",
    level: kindDef.support === "Limited surface" ? "warn" : "pass",
    title: "Google surface",
    detail:
      kindDef.support === "Limited surface"
        ? "This type can be valid Schema.org markup while rich-result visibility is limited by Google policy and page context."
        : "This type is listed in Google Search structured data documentation."
  });
  checks.push({
    key: "handoff",
    level: "pass",
    title: "Handoff",
    detail: "Script tag, raw JSON, and official testing links are available."
  });

  return {
    score,
    label: score >= 90 ? "Ready" : score >= 74 ? "Close" : score >= 50 ? "Needs fields" : "Incomplete",
    requiredDone,
    requiredTotal: required.length,
    recommendedDone,
    recommendedTotal: recommended.length,
    checks
  };
}

export function scriptTag(schema: Record<string, unknown>, pretty: boolean): string {
  const body = JSON.stringify(schema, null, pretty ? 2 : 0);
  return `<script type="application/ld+json">\n${body}\n</script>`;
}

export function rawJson(schema: Record<string, unknown>, pretty: boolean): string {
  return JSON.stringify(schema, null, pretty ? 2 : 0);
}

function compactObject(value: unknown): Record<string, unknown> {
  if (!isPlainObject(value)) return {};

  const output: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (typeof child === "string") {
      if (child.trim()) output[key] = child.trim();
      continue;
    }
    if (typeof child === "number" && Number.isFinite(child)) {
      output[key] = child;
      continue;
    }
    if (Array.isArray(child)) {
      const items = child
        .map((item) => (isPlainObject(item) ? compactObject(item) : item))
        .filter((item) => {
          if (typeof item === "string") return item.trim().length > 0;
          if (isPlainObject(item)) return Object.keys(item).length > 0;
          return item !== undefined && item !== null;
        });
      if (items.length) output[key] = items;
      continue;
    }
    if (isPlainObject(child)) {
      const compacted = compactObject(child);
      if (Object.keys(compacted).length) output[key] = compacted;
    }
  }
  return output;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function splitList(value: string | undefined): string[] {
  return (value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function isFilled(value: string | undefined): boolean {
  return Boolean(value && value.trim());
}
